import { CalendarDays, ChevronRight, LoaderCircle, MessageCircle, Radio, RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { formatLocalAirtime } from './countdownTime'
import { mergeRoomSummaries, roomPageOffset, roomSelectionForRoom, roomStatusLabel, roomSummariesFromPayload, type LiveRoomSummary, type RoomSelection } from './liveRoomDirectory'

export default function LiveRoomDirectory({ viewerKey, onOpenRoom }: {viewerKey: string; onOpenRoom: (room: RoomSelection) => void}) {
  const [rooms, setRooms] = useState<LiveRoomSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [nextOffset, setNextOffset] = useState<number | null>(null)
  const [revision, setRevision] = useState(0)
  const controllerRef = useRef<AbortController | null>(null)
  const busyRef = useRef(false)

  async function loadPage(offset: number, controller: AbortController) {
    if (busyRef.current || controller.signal.aborted) return
    busyRef.current = true
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`/api/live?limit=30&offset=${offset}`, {credentials:'same-origin',cache:'no-store',signal:controller.signal})
      if (!response.ok) throw new Error('Rooms could not load. Please try again.')
      const payload: unknown = await response.json()
      const incoming = roomSummariesFromPayload(payload)
      const next = roomPageOffset(payload)
      if (next !== null && next <= offset) throw new Error('The next room-list page could not be verified.')
      if (controller.signal.aborted || controllerRef.current !== controller) return
      setRooms((current) => offset === 0 ? incoming : mergeRoomSummaries(current,incoming))
      setNextOffset(next)
    } catch (requestError) {
      if (!controller.signal.aborted && controllerRef.current === controller) setError(requestError instanceof Error ? requestError.message : 'Rooms could not load. Please try again.')
    } finally {
      if (!controller.signal.aborted && controllerRef.current === controller) {busyRef.current = false; setLoading(false)}
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    controllerRef.current = controller
    busyRef.current = false
    setRooms([])
    setNextOffset(null)
    void loadPage(0,controller)
    return () => {controller.abort(); if (controllerRef.current === controller) controllerRef.current = null}
  }, [viewerKey,revision])

  return <div className="live-room-directory">
    <header className="live-room-directory__heading">
      <span className="eyebrow">FIND YOUR CONVERSATION</span>
      <h2>Live rooms</h2>
      <p>Pick a show, find your fellow fans, and settle in for the drama.</p>
    </header>
    <div className="live-room-directory__list" aria-label="Available live rooms">
      {loading && <p className="live-room-directory__status" role="status"><LoaderCircle size={18} className="spin" />{rooms.length ? 'Loading more rooms…' : 'Loading rooms…'}</p>}
      {!loading && !error && rooms.length === 0 && <p className="live-room-directory__status">No rooms are available right now. Check back for the next watch-along.</p>}
      {rooms.map((room) => <article key={room.roomSlug} className={`live-room-directory__card live-room-directory__card--${room.accent}`}>
        <header><span className="live-room-directory__icon"><Radio size={24} /></span><span className={`live-room-directory__state${room.publicCanPost ? ' is-open' : ''}`}>{roomStatusLabel(room)}</span></header>
        <h3>{room.title}{room.season !== null && <span>Season {room.season}</span>}</h3>
        {room.isDemo && <small className="live-room-directory__demo">Demo room</small>}
        <p>{room.description || 'A fan watch-along and conversation room.'}</p>
        <div className="live-room-directory__times">
          {room.startsAt && <span><CalendarDays size={15} /><span>{room.state === 'pre' ? 'Next show' : 'Showtime'} <strong>{formatLocalAirtime(new Date(room.startsAt))}</strong></span></span>}
          {room.publicCanPost && room.locksAt ? <small>Member chat closes {formatLocalAirtime(new Date(room.locksAt))}</small> : room.opensAt && <small>Member chat opening: {formatLocalAirtime(new Date(room.opensAt))}</small>}
        </div>
        <footer><span><MessageCircle size={14} />{room.messageCount} {room.messageCount === 1 ? 'message' : 'messages'}</span><button type="button" aria-label={`Open ${room.title} room`} onClick={() => onOpenRoom(roomSelectionForRoom(room))}>Open room <ChevronRight size={16} /></button></footer>
      </article>)}
      {error && <p className="live-room-directory__error" role="alert">{error}</p>}
      {error && rooms.length === 0 && <button className="live-room-directory__load" type="button" disabled={loading} onClick={() => setRevision((current) => current + 1)}><RefreshCw size={15} />Retry rooms</button>}
      {nextOffset !== null && <button className="live-room-directory__load" type="button" disabled={loading} onClick={() => {const controller = controllerRef.current; if (controller) void loadPage(nextOffset,controller)}}>Load more rooms <ChevronRight size={16} /></button>}
    </div>
    <footer className="live-room-directory__note">All times use your device’s timezone. You can read a room before member chat opens.</footer>
  </div>
}
